import React from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  noIndex?: boolean;
  image?: string;
  jsonLd?: Record<string, any> | Record<string, any>[]; // structured data
}

const defaultMeta = {
  title: 'IOS Relocations',
  description: 'Corporate housing and relocation platform.',
  image: '/android-chrome-192x192.png'
};

const SEO: React.FC<SEOProps> = ({ title, description, canonical, noIndex, image, jsonLd }) => {
  const metaTitle = title ? `${title} | IOS Relocations` : defaultMeta.title;
  const metaDescription = description || defaultMeta.description;
  const metaImage = image || defaultMeta.image;

  // Direct DOM side-effects (CSR only). This avoids peer dependency issues.
  React.useEffect(() => {
    if (typeof document !== 'undefined') {
      document.title = metaTitle;
      const setMeta = (attr: { name?: string; property?: string; content: string }) => {
        const selector = attr.name ? `meta[name='${attr.name}']` : `meta[property='${attr.property}']`;
        let el = document.head.querySelector(selector) as HTMLMetaElement | null;
        if (!el) {
          el = document.createElement('meta');
          if (attr.name) el.setAttribute('name', attr.name);
          if (attr.property) el.setAttribute('property', attr.property);
          document.head.appendChild(el);
        }
        el.setAttribute('content', attr.content);
      };
      setMeta({ name: 'description', content: metaDescription });
      if (noIndex) setMeta({ name: 'robots', content: 'noindex,nofollow' });
      setMeta({ property: 'og:title', content: metaTitle });
      setMeta({ property: 'og:description', content: metaDescription });
      setMeta({ property: 'og:image', content: metaImage });
      setMeta({ property: 'og:type', content: 'website' });
      setMeta({ name: 'twitter:card', content: 'summary_large_image' });
      setMeta({ name: 'twitter:title', content: metaTitle });
      setMeta({ name: 'twitter:description', content: metaDescription });
      setMeta({ name: 'twitter:image', content: metaImage });
      if (canonical) {
        let link: HTMLLinkElement | null = document.head.querySelector("link[rel='canonical']");
        if (!link) {
          link = document.createElement('link');
          link.setAttribute('rel', 'canonical');
          document.head.appendChild(link);
        }
        link.setAttribute('href', canonical);
      }
    }
  }, [metaTitle, metaDescription, metaImage, canonical, noIndex]);

  return jsonLd ? (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
  ) : null;
};

export default SEO;